import React, { useEffect, useState } from 'react';
import { splitsFormules, heeftFormule } from '../utils/formuleTekst';
import './VraagTekst.css';

/**
 * VraagTekst — vraagtekst, antwoordoptie of antwoordmodel met formules.
 *
 * Formules staan in LaTeX tussen $…$ (inline) of $$…$$ (blok) en worden met
 * KaTeX gerenderd. Zonder dollarteken rendert dit gewoon de tekst, zonder
 * KaTeX te laden; met dollarteken wordt KaTeX (gebundeld, geen CDN: de
 * iPad- en Windows-app laten alleen testwisely.nl toe) één keer geladen.
 *
 * Veiligheid: alleen de KaTeX-uitvoer gaat via dangerouslySetInnerHTML.
 * De invoer van KaTeX is TeX, geen HTML; met trust:false zijn \url, \href en
 * \htmlClass uitgeschakeld en throwOnError:false toont kapotte TeX in rood in
 * plaats van te crashen. Tekstsegmenten blijven gewone React-tekst.
 */

let katexModule = null;
let katexLader = null;

function laadKatex() {
  if (katexModule) return Promise.resolve(katexModule);
  if (!katexLader) {
    katexLader = Promise.all([import('katex'), import('katex/dist/katex.min.css')])
      .then(([mod]) => { katexModule = mod.default || mod; return katexModule; });
  }
  return katexLader;
}

function useKatex(nodig) {
  const [katex, setKatex] = useState(katexModule);
  useEffect(() => {
    if (!nodig || katex) return undefined;
    let actief = true;
    laadKatex().then(k => { if (actief) setKatex(k); }).catch(() => { /* zonder KaTeX blijft de ruwe tekst staan */ });
    return () => { actief = false; };
  }, [nodig, katex]);
  return katex;
}

const KATEX_OPTIES = { throwOnError: false, trust: false, strict: 'ignore', maxExpand: 1000, maxSize: 50 };

function Formule({ katex, inhoud, blok }) {
  if (!katex) return <span className="vt-formule-ruw">{blok ? `$$${inhoud}$$` : `$${inhoud}$`}</span>;
  const html = katex.renderToString(inhoud, { ...KATEX_OPTIES, displayMode: blok });
  return blok
    ? <span className="vt-blok" dangerouslySetInnerHTML={{ __html: html }} />
    : <span className="vt-inline" dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * @param {string} tekst
 * @param {string} [as='span']  element om in te renderen ('p', 'span', 'div', ...)
 */
export default function VraagTekst({ tekst, as: Tag = 'span', className, style, ...rest }) {
  const nodig = heeftFormule(tekst);
  const katex = useKatex(nodig);
  if (!nodig) return <Tag className={className} style={style} {...rest}>{tekst}</Tag>;
  const segmenten = splitsFormules(tekst);
  return (
    <Tag className={`vt-met-formules${className ? ' ' + className : ''}`} style={style} {...rest}>
      {segmenten.map((s, i) => (
        s.soort === 'tekst'
          ? <React.Fragment key={i}>{s.inhoud}</React.Fragment>
          : <Formule key={i} katex={katex} inhoud={s.inhoud} blok={s.soort === 'blok'} />
      ))}
    </Tag>
  );
}
